'use client';

import { Typography } from '@mui/material';
import LocalizedLink from '../Link/localized-link';
import { usePathname } from 'next/navigation';
import HomeIcon from '@mui/icons-material/Home';
import { useMemo } from 'react';
import { useAppSelector } from '../../hooks/reduxHooks';
import { StyledBreadcrumbs } from './Breadcrumbs.styles';

export default function Breadcrumbs({
  finalCrumbTitle,
  customBreadcrumbs,
}: {
  finalCrumbTitle?: string;
  customBreadcrumbs?: { title: string; href?: string }[];
}) {
  const pathname = usePathname();

  const locale = useAppSelector((st) => st.locale);

  const messages = useMemo(() => {
    return {
      home: 'Chrono 10:10',
      aboutUs: locale['heromock_about_us_title'],
      brands: locale['heromock_brands_title'],
      sellTrade: locale['sell_trade_link_sell_trade_page'],
      sellTradeOffer1: locale['header_items_sell'],
      sellTradeOffer2: locale['header_items_trade'],
      consignment: locale['header_items_consignment'],
      serviceCenter: locale['footer_navlink_item_3'],
      blog: locale['header_items_5'],
      allWatches: locale['heromock_all_watches_title'],
      newArrivals: locale['heromock_new_arrivals_title'],
      contact: locale['contact_us'],
      wishlist: locale['wishlist_header_title'],
      compare: locale['compare_page_title'],
    };
  }, [locale]);

  // Mapping of route segments to titles
  const routeTitleMapping: { [key: string]: string } = {
    '': messages.home,
    'about-us': messages.aboutUs,
    brands: messages.brands,
    'sell-trade': messages.sellTrade,
    sellTradeOffer1: messages.sellTradeOffer1,
    sellTradeOffer2: messages.sellTradeOffer2,
    consignment: messages.consignment,
    'service-page': messages.serviceCenter,
    'all-watches': messages.allWatches,
    'new-arrivals': messages.newArrivals,
    blog: messages.blog,
    'contact-us': messages.contact,
    wishlist: messages.wishlist,
    compare: messages.compare,
  };

  let breadcrumbs = null;
  if (customBreadcrumbs && customBreadcrumbs.length > 0) {
    breadcrumbs = customBreadcrumbs.map(({ title, href }, index) => {
      const isLast = index === customBreadcrumbs.length - 1;

      return isLast ? (
        <Typography key={title} sx={{ color: 'text.primary' }}>
          {finalCrumbTitle || title}
        </Typography>
      ) : (
        <LocalizedLink key={title} color='inherit' href={href!}>
          {title}
        </LocalizedLink>
      );
    });
  } else {
    // Split the path into segments
    const pathSegments = pathname.split('/').filter((segment) => segment && segment !== 'cz' && segment !== 'tag');

    // Build the breadcrumb links
    breadcrumbs = pathSegments.map((segment, index) => {
      const href = '/' + pathSegments.slice(0, index + 1).join('/');
      const isLast = index === pathSegments.length - 1;
      let title = '';
      if (segment === '2' && pathSegments.includes('sell-trade')) {
        title = messages.sellTradeOffer1;
      } else if (segment === '1' && pathSegments.includes('sell-trade')) {
        title = messages.sellTradeOffer2;
      } else {
        title = routeTitleMapping[segment] || segment; // Use mapping or fallback to segment
      }

      return isLast ? (
        <Typography key={title} sx={{ color: 'text.primary' }}>
          {finalCrumbTitle || title}
        </Typography>
      ) : (
        <LocalizedLink key={title} color='inherit' href={href}>
          {title}
        </LocalizedLink>
      );
    });
  }

  return (
    <StyledBreadcrumbs aria-label='breadcrumb'>
      <LocalizedLink
        color='inherit'
        href='/'
        style={{ display: 'flex', alignItems: 'center' }}
        key={'initial-breadcrumb'}
      >
        <HomeIcon sx={{ mr: 0.5 }} fontSize='inherit' />
        {routeTitleMapping['']}
      </LocalizedLink>
      {breadcrumbs}
    </StyledBreadcrumbs>
  );
}
